import { GoogleGenerativeAI } from "@google/generative-ai";
import { HfInference } from "@huggingface/inference";
import axios from "axios";

// ==================== CONFIGURATION ====================

// Gemini Models (try in order)
const GEMINI_MODELS = [
  "gemini-2.5-flash",
  "gemini-2.0-flash",
  "gemini-flash-latest",
  "gemini-2.5-pro",
  "gemini-pro-latest",
];

// Hugging Face Models (try in order)
const HF_MODELS = [
  "llava-hf/llava-1.5-7b-hf", // LLaVA 1.5 7B
  "Qwen/Qwen-VL", // Qwen Vision Language
  "Salesforce/blip2-opt-2.7b", // BLIP-2
];

// Groq Models (FREE - no credit card required)
const GROQ_MODELS = [
  "llama-3.2-11b-vision-preview", // Llama 3.2 11B Vision
  "llama-3.2-90b-vision-preview", // Llama 3.2 90B Vision
];

// Together AI Models (OPTIONAL - requires $5 credit deposit)
// Only used if REACT_APP_TOGETHER_API_KEY is provided
const TOGETHER_MODELS = [
  "meta-llama/Llama-3.2-11B-Vision-Instruct-Turbo",
  "Qwen/Qwen-VL-72B-Instruct",
];

// ==================== UTILITY FUNCTIONS ====================

const getApiKey = (provider) => {
  switch (provider) {
    case "gemini":
      return import.meta.env.REACT_APP_GEMINI_API_KEY || import.meta.env.VITE_GEMINI_API_KEY;
    case "huggingface":
      return import.meta.env.REACT_APP_HUGGINGFACE_API_KEY || import.meta.env.VITE_HUGGINGFACE_API_KEY;
    case "groq":
      return import.meta.env.REACT_APP_GROQ_API_KEY || import.meta.env.VITE_GROQ_API_KEY;
    case "together":
      return import.meta.env.REACT_APP_TOGETHER_API_KEY || import.meta.env.VITE_TOGETHER_API_KEY;
    default:
      return null;
  }
};

const fileToBase64 = async (file) => {
  const arrayBuffer = await file.arrayBuffer();
  const uint8Array = new Uint8Array(arrayBuffer);
  const binaryString = uint8Array.reduce(
    (acc, byte) => acc + String.fromCharCode(byte),
    ""
  );
  return btoa(binaryString);
};

const fileToGenerativePart = async (file) => {
  const base64 = await fileToBase64(file);
  return {
    inlineData: {
      data: base64,
      mimeType: file.type || "image/jpeg",
    },
  };
};

const buildPrompt = () => {
  return `You are an expert visual analyst and coach. Analyze the provided photo and respond ONLY with JSON following this exact shape:
{
  "personalityTraits": {
    "confidence": "<brief text>",
    "approachability": "<brief text>",
    "professionalism": "<brief text>",
    "charisma": "<brief text>",
    "summary": "<1-2 sentence summary>"
  },
  "posture": {
    "alignment": "<brief text>",
    "bodyLanguage": "<brief text>",
    "gaze": "<brief text>",
    "hands": "<brief text>",
    "overall": "<brief text>"
  },
  "attractiveness": {
    "score": <number 1-10>,
    "strengths": ["<strength 1>", "<strength 2>"],
    "areasToImprove": ["<area 1>", "<area 2>"]
  },
  "styleAndGrooming": {
    "outfit": "<brief text>",
    "grooming": "<brief text>",
    "lighting": "<brief text>",
    "background": "<brief text>"
  },
  "suggestions": {
    "posture": ["<tip>", "<tip>"],
    "appearance": ["<tip>", "<tip>"],
    "presentation": ["<tip>", "<tip>"]
  }
}

Rules:
- Keep each text field concise (max 20 words).
- Make suggestions specific and actionable.
- If something is unclear in the image, say "uncertain".
- Respond ONLY with valid JSON, no markdown, no code fences.`;
};

const parseJsonSafe = (text) => {
  if (!text) return null;

  // Remove code fences and markdown
  const cleaned = text
    .replace(/```json/gi, "```")
    .replace(/```/g, "")
    .trim();

  // Try to extract JSON from text if it's wrapped
  const jsonMatch = cleaned.match(/\{[\s\S]*\}/);
  const jsonString = jsonMatch ? jsonMatch[0] : cleaned;

  try {
    return JSON.parse(jsonString);
  } catch (err) {
    console.warn("JSON parse error:", err.message);
    return null;
  }
};

// ==================== PROVIDER IMPLEMENTATIONS ====================

// 1. Gemini Provider
const tryGemini = async (file, prompt) => {
  const apiKey = getApiKey("gemini");
  if (!apiKey) {
    throw new Error("Gemini API key not configured");
  }

  const genAI = new GoogleGenerativeAI(apiKey);
  const imagePart = await fileToGenerativePart(file);

  for (const modelId of GEMINI_MODELS) {
    try {
      const model = genAI.getGenerativeModel({ model: modelId });
      const result = await model.generateContent([prompt, imagePart]);
      const text = result?.response?.text();
      const parsed = parseJsonSafe(text);

      if (parsed) {
        return parsed;
      }
    } catch (error) {
      // Skip quota errors and continue to next model
      if (error.message?.includes("quota") || error.message?.includes("429")) {
        console.warn(`✗ Gemini ${modelId} quota exceeded`);
        continue;
      }
      console.warn(`✗ Gemini ${modelId} failed:`, error.message);
    }
  }

  throw new Error("All Gemini models failed");
};

// 2. Hugging Face Provider
const tryHuggingFace = async (file, prompt) => {
  const apiKey = getApiKey("huggingface");
  if (!apiKey) {
    throw new Error("Hugging Face API key not configured");
  }

  const hf = new HfInference(apiKey);
  const base64Image = await fileToBase64(file);

  for (const modelId of HF_MODELS) {
    try {
      console.log(`Trying Hugging Face model: ${modelId}`);

      // Convert file to blob for Hugging Face API
      const arrayBuffer = await file.arrayBuffer();
      const blob = new Blob([arrayBuffer], { type: file.type || "image/jpeg" });

      // Try imageToText API (works for most vision models)
      const response = await hf.imageToText({
        model: modelId,
        inputs: blob,
        parameters: {
          prompt: prompt,
        },
      });

      const text = response?.generated_text || response?.text || "";

      if (!text) {
        continue;
      }

      const parsed = parseJsonSafe(text);

      if (parsed) {
        return parsed;
      }

      // If response is text but not JSON, try to extract JSON
      const extracted = parseJsonSafe(text);
      if (extracted) {
        return extracted;
      }
    } catch (error) {
      console.warn(`✗ Hugging Face ${modelId} failed:`, error.message);
      // Continue to next model
      continue;
    }
  }

  throw new Error("All Hugging Face models failed");
};

// 3. Groq Provider (FREE - no credit card required)
const tryGroq = async (file, prompt) => {
  const apiKey = getApiKey("groq");
  if (!apiKey) {
    throw new Error("Groq API key not configured");
  }

  const base64Image = await fileToBase64(file);
  const imageDataUrl = `data:${file.type || "image/jpeg"};base64,${base64Image}`;

  for (const modelId of GROQ_MODELS) {
    try {
      console.log(`Trying Groq model: ${modelId}`);

      const response = await axios.post(
        "https://api.groq.com/openai/v1/chat/completions",
        {
          model: modelId,
          messages: [
            {
              role: "user",
              content: [
                {
                  type: "image_url",
                  image_url: {
                    url: imageDataUrl,
                  },
                },
                {
                  type: "text",
                  text: prompt,
                },
              ],
            },
          ],
          temperature: 0.7,
          max_tokens: 2000,
        },
        {
          headers: {
            Authorization: `Bearer ${apiKey}`,
            "Content-Type": "application/json",
          },
        }
      );

      const text = response.data?.choices?.[0]?.message?.content || "";

      const parsed = parseJsonSafe(text);

      if (parsed) {
        return parsed;
      }

      // Try to extract JSON from response
      if (text) {
        const extracted = parseJsonSafe(text);
        if (extracted) {
          return extracted;
        }
      }
    } catch (error) {
      console.warn(`✗ Groq ${modelId} failed:`, error.message);
      // Continue to next model
      continue;
    }
  }

  throw new Error("All Groq models failed");
};

// 4. Together AI Provider (OPTIONAL - requires $5 credit deposit)
const tryTogetherAI = async (file, prompt) => {
  const apiKey = getApiKey("together");
  if (!apiKey) {
    throw new Error("Together AI API key not configured (requires $5 credit deposit)");
  }

  const base64Image = await fileToBase64(file);
  const imageDataUrl = `data:${file.type || "image/jpeg"};base64,${base64Image}`;

  for (const modelId of TOGETHER_MODELS) {
    try {
      console.log(`Trying Together AI model: ${modelId}`);

      const response = await axios.post(
        "https://api.together.xyz/v1/chat/completions",
        {
          model: modelId,
          messages: [
            {
              role: "user",
              content: [
                {
                  type: "image_url",
                  image_url: {
                    url: imageDataUrl,
                  },
                },
                {
                  type: "text",
                  text: prompt,
                },
              ],
            },
          ],
          temperature: 0.7,
          max_tokens: 2000,
        },
        {
          headers: {
            Authorization: `Bearer ${apiKey}`,
            "Content-Type": "application/json",
          },
        }
      );

      const text =
        response.data?.choices?.[0]?.message?.content ||
        response.data?.output?.choices?.[0]?.text ||
        "";

      const parsed = parseJsonSafe(text);

      if (parsed) {
        return parsed;
      }

      // Try to extract JSON from response
      if (text) {
        const extracted = parseJsonSafe(text);
        if (extracted) {
          return extracted;
        }
      }
    } catch (error) {
      console.warn(`✗ Together AI ${modelId} failed:`, error.message);
      // Continue to next model
      continue;
    }
  }

  throw new Error("All Together AI models failed");
};

// ==================== MAIN EXPORT ====================

export const analyzeImage = async (file) => {
  if (!file) {
    throw new Error("No image file provided.");
  }

  const prompt = buildPrompt();
  const errors = [];

  // Try providers in order: Gemini → Groq → Hugging Face → Together AI (optional)
  const providers = [];

  // 1. Gemini first (primary provider)
  providers.push({ name: "Gemini", fn: tryGemini });

  // 2. Groq second (FREE - no credit card required, fallback)
  if (getApiKey("groq")) {
    providers.push({ name: "Groq", fn: tryGroq });
  }

  // 3. Hugging Face third (FREE - open source, fallback)
  providers.push({ name: "Hugging Face", fn: tryHuggingFace });

  // 4. Together AI (optional - requires $5 credit deposit)
  if (getApiKey("together")) {
    providers.push({ name: "Together AI", fn: tryTogetherAI });
  }

  for (const provider of providers) {
    try {
      const result = await provider.fn(file, prompt);
      if (result) {
        console.log(`✓ Successfully analyzed image using ${provider.name}`);
        return result;
      }
    } catch (error) {
      console.warn(`✗ ${provider.name} failed:`, error.message);
      errors.push(`${provider.name}: ${error.message}`);
      // Continue to next provider
      continue;
    }
  }

  // All providers failed
  const errorMessage = `All image analysis providers failed:\n${errors.join("\n")}\n\nPlease check your API keys and try again.`;
  console.error(errorMessage);
  throw new Error(errorMessage);
};

// Helper function to create empty analysis structure
export const createEmptyAnalysis = () => ({
  personalityTraits: {
    confidence: "",
    approachability: "",
    professionalism: "",
    charisma: "",
    summary: "",
  },
  posture: {
    alignment: "",
    bodyLanguage: "",
    gaze: "",
    hands: "",
    overall: "",
  },
  attractiveness: {
    score: null,
    strengths: [],
    areasToImprove: [],
  },
  styleAndGrooming: {
    outfit: "",
    grooming: "",
    lighting: "",
    background: "",
  },
  suggestions: {
    posture: [],
    appearance: [],
    presentation: [],
  },
});
